import { z } from 'zod'

export const ClientCampaignStatus = Object.freeze({
  active: 'ACTIVE',
  paused: 'PAUSED',
})

export const GoogleCampaignStatus = Object.freeze({
  enabled: 'ENABLED',
  paused: 'PAUSED',
})

export const MetaCampaignStatus = Object.freeze({
  active: 'ACTIVE',
  paused: 'PAUSED',
})

const taskSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
  })
  .passthrough()
  .nullable()

const googleAdGroupSchema = z
  .object({
    tc: z.string(),
    tc_run_id: z.number(),
    ad_group_id: z.string().nullable().optional(),
    status: z.enum(['ENABLED', 'PAUSED']).nullable().optional(),
  })
  .passthrough()

const googleAdGroupStatusSchema = z.object({
  tc: z.string(),
  tc_run_id: z.number(),
  status: z.enum(['ENABLED', 'PAUSED']),
})

// The operator's last-requested state for a platform — null until the first
// PUT that targets it. Distinct from campaign.status, which is synced from
// the ad platform itself and may lag behind intent.
const googleIntentSchema = z
  .object({
    campaign_status: z.enum(['ENABLED', 'PAUSED']).nullable().optional(),
    ad_group_statuses: z.array(googleAdGroupStatusSchema).nullable().optional(),
  })
  .passthrough()
  .nullable()

const googlePlatformBlockSchema = z
  .object({
    campaign: z
      .object({
        status: z.enum(['ENABLED', 'PAUSED']).nullable().optional(),
        start_date: z.string().nullable().optional(),
        end_date: z.string().nullable().optional(),
      })
      .passthrough(),
    ad_groups: z.array(googleAdGroupSchema).optional(),
    task: taskSchema.optional(),
    intent: googleIntentSchema.optional(),
  })
  .passthrough()

const metaAdSetSchema = z
  .object({
    tc: z.string(),
    tc_run_id: z.number(),
    adset_id: z.string().nullable().optional(),
    status: z.enum(['ACTIVE', 'PAUSED']).nullable().optional(),
  })
  .passthrough()

const metaAdSetStatusSchema = z.object({
  tc: z.string(),
  tc_run_id: z.number(),
  status: z.enum(['ACTIVE', 'PAUSED']),
})

// meta.ad_set_statuses is accepted/stored (surfaced here as intent) but not
// yet dispatched to the Integration Worker at the per-adset level — only
// campaign-level Meta control is live. See mappers.js/api.js call sites for
// where this constraint is called out for consumers.
const metaIntentSchema = z
  .object({
    campaign_status: z.enum(['ACTIVE', 'PAUSED']).nullable().optional(),
    ad_set_statuses: z.array(metaAdSetStatusSchema).nullable().optional(),
  })
  .passthrough()
  .nullable()

const metaPlatformBlockSchema = z
  .object({
    campaign: z
      .object({
        status: z.enum(['ACTIVE', 'PAUSED']).nullable().optional(),
        start_time: z.string().nullable().optional(),
        stop_time: z.string().nullable().optional(),
      })
      .passthrough(),
    ad_sets: z.array(metaAdSetSchema).optional(),
    task: taskSchema.optional(),
    intent: metaIntentSchema.optional(),
  })
  .passthrough()

// A platform key absent from `platforms` means "not yet published/synced on
// that platform" — distinct from "paused". Do not default missing keys to
// an empty block; mappers.js relies on the key's presence/absence itself.
export const campaignStatusResponseSchema = z
  .object({
    client_campaign_status: z.enum(['ACTIVE', 'PAUSED']).nullable().optional(),
    campaign_status: z.string().nullable().optional(),
    campaign_start_time: z.string().nullable().optional(),
    campaign_stop_time: z.string().nullable().optional(),
    task: taskSchema.optional(),
    platforms: z
      .object({
        google: googlePlatformBlockSchema.optional(),
        meta: metaPlatformBlockSchema.optional(),
      })
      .passthrough(),
  })
  .passthrough()

const googleOverrideSchema = z
  .object({
    campaign_status: z.enum(['ENABLED', 'PAUSED']).optional(),
    ad_group_statuses: z.array(googleAdGroupStatusSchema).optional(),
  })
  .strict()

const metaOverrideSchema = z
  .object({
    campaign_status: z.enum(['ACTIVE', 'PAUSED']).optional(),
    ad_set_statuses: z.array(metaAdSetStatusSchema).optional(),
  })
  .strict()

// PUT /campaign_status body. Backend requires at least one of the three top-
// level fields (400 on an empty body) — enforced here too so callers fail
// fast client-side with the same rule.
export const campaignStatusRequestSchema = z
  .object({
    client_campaign_status: z.enum(['ACTIVE', 'PAUSED']).optional(),
    google: googleOverrideSchema.optional(),
    meta: metaOverrideSchema.optional(),
  })
  .strict()
  .refine((body) => body.client_campaign_status || body.google || body.meta, {
    message: 'At least one of client_campaign_status, google, or meta must be provided',
  })
